import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BenutzerprofilService } from 'app/core/services/benutzerprofil/benutzerprofil.service';
import { BenutzerProfil, Lieferant } from 'app/core/services/api-client';
import { ClientInfo } from 'app/core/services/benutzerprofil/models/clientInfo';
import { FavoriteMerkmal, StoredFavoriteMerkmal } from '@models/dossier/dossier-models';
import { IViews } from 'app/modules/shared-area/ducks/panel/models/IViews';
import { IView } from 'app/modules/shared-area/ducks/panel/models/IView';
import { TranslateService } from '@ngx-translate/core';
import * as PanelActions from 'app/modules/shared-area/ducks/panel/store/panel.actions';
import * as _ from 'lodash';
import * as UserProfileActions from './user-profile.actions';
import { IAppState } from 'app/modules/shared-area/root-state';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable()
export class UserProfileEffects {
  constructor(
    private actions$: Actions,
    private store: Store<IAppState>,
    private readonly service: BenutzerprofilService,
    private translateService: TranslateService,
  ) {}

  public async loadUserProfilesAsync(): Promise<void> {
    this.store.dispatch(UserProfileActions.userProfileRequest());
    try {
      const [profil, lieferant] = await Promise.all([
        this.service.loadUserProfile().toPromise(),
        this.service.loadMeinLieferant().toPromise(),
      ]);

      let namedViews: IViews = (profil.clientInfo && profil.clientInfo.namedViews) || null; // Bug 60373, kein Default-View gewünscht

      const hasDefault = _.some(namedViews, (v: { view: IView }) => v.view.isDefaultView);

      namedViews = {
        ...this.getSystemViews(!hasDefault),
        ...namedViews,
      };

      this.store.dispatch(PanelActions.setNamedViews({ views: namedViews }));
      this.store.dispatch(PanelActions.applyDefaultView());
      
      const storedFavouriteMerkmale: StoredFavoriteMerkmal[] = localStorage.getItem('favouriteMerkmale')
        ? JSON.parse(localStorage.getItem('favouriteMerkmale'))
        : [];

      let favouriteMerkmale: StoredFavoriteMerkmal[] = storedFavouriteMerkmale;
      if (storedFavouriteMerkmale.length > 0) {
        try {
          const keys = storedFavouriteMerkmale.map(f => f.key);
          const merkmalInfos = await this.service.getMerkmalInfo(keys).toPromise();
          const infoMap = new Map(merkmalInfos.map(info => [info.name, info.displayName]));
          favouriteMerkmale = storedFavouriteMerkmale
            .filter(f => infoMap.has(f.key))
            .map(({ key, timestamp }) => ({ key, displayName: infoMap.get(key), timestamp }));
          localStorage.setItem('favouriteMerkmale', JSON.stringify(favouriteMerkmale));
        } catch {
          // If apiDatenmodellMerkmalInfoPost fails, use original favouriteMerkmale unchanged
        }
      }

      const response = {
        fachlektorMerkmale: new Set<number>(profil.fachlektorMerkmale),
        lieferantenMerkmale: new Set<number>(lieferant.merkmalIds),
        clientInfo: profil.clientInfo,
        favouriteMerkmale,
      };
      this.store.dispatch(UserProfileActions.userProfileResponse(response));
    } catch (error) {
      this.store.dispatch(UserProfileActions.userProfileError({ error: error.toString() }));
    }
  }

  public async addFavouriteMerkmalAsync(key: string) {
    const favouriteMerkmale = await this.service.addFavouriteMerkmal(key).toPromise();
    this.store.dispatch(UserProfileActions.setFavoriteMerkmale({ newFavoriteMerkmale: favouriteMerkmale }));
  }

  public async saveClientInfoAsync(clientInfo: ClientInfo) {
    try {
      await this.service.saveClientInfo(clientInfo).toPromise();
      this.store.dispatch(UserProfileActions.saveClientInfoSuccess());
    } catch (error) {
      this.store.dispatch(UserProfileActions.saveClientInfoError());
    }
  }

  private getSystemViews(isDefault: boolean): IViews {
    const viewNameAllAttributesVisible = this.translateService.instant(
      'components.dossier.system.allAttributesVisible',
    );
    const viewNameAllAttributesButCollapsed = this.translateService.instant(
      'components.dossier.system.allAttributesButCollapsed',
    );
    const systemViews = {
      [viewNameAllAttributesVisible]: {
        view: {
          id: viewNameAllAttributesVisible,
          viewName: viewNameAllAttributesVisible,
          isDefaultView: false,
          showAllMerkmale: true,
          isSystem: true,
          shownMerkmale: {},
          panels: {},
          openAllPanels: true,
        },
      },
      [viewNameAllAttributesButCollapsed]: {
        view: {
          id: viewNameAllAttributesButCollapsed,
          viewName: viewNameAllAttributesButCollapsed,
          isDefaultView: isDefault,
          showAllMerkmale: true,
          isSystem: true,
          shownMerkmale: {},
          panels: {},
          openAllPanels: false,
        },
      },
    };
    return systemViews;
  }

  loadFavoriten$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.loadFavoriten),
      switchMap(() =>
        this.service.loadFavoriten().pipe(
          map(favoriten => UserProfileActions.loadFavoritenSuccess({ favoriten })),
          catchError(error =>
            of(UserProfileActions.loadFavoritenFailure({ error: error.toString() }))
          )
        )
      )
    )
  );

  addFavorit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.addFavorit),
      switchMap(({ objektTyp, objektId }) =>
        this.service.addFavorit(objektTyp, objektId).pipe(
          map(favorit => UserProfileActions.addFavoritSuccess({ favorit })),
          catchError(error =>
            of(UserProfileActions.addFavoritFailure({ error: error.toString() }))
          )
        )
      )
    )
  );

  removeFavorit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.removeFavorit),
      switchMap(({ objektTyp, objektId }) =>
        this.service.removeFavorit(objektTyp, objektId).pipe(
          map(() => UserProfileActions.removeFavoritSuccess({ objektTyp, objektId })),
          catchError(error =>
            of(UserProfileActions.removeFavoritFailure({ error: error.toString() }))
          )
        )
      )
    )
  );

  checkFavorit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserProfileActions.checkFavorit),
      switchMap(({ objektTyp, objektId }) =>
        this.service.checkFavorit(objektTyp, objektId).pipe(
          map(favoriten => {
            // Service returns array with 0 or 1 elements
            const favorit = favoriten.length > 0 ? favoriten[0] : null;
            return UserProfileActions.checkFavoritSuccess({ objektTyp, objektId, favorit });
          }),
          catchError(error =>
            of(UserProfileActions.checkFavoritFailure({ error: error.toString() }))
          )
        )
      )
    )
  );
}
